import { UploadDragger } from "@/components/upload-dragger/UploadDragger";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { tableStandardProps } from "@/utils/componentUtils";
import { formatFileSize, formatLocaleDateTime } from "@/utils/formatUtils";
import { Col, Divider, Popconfirm, Row, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { RcFile } from "antd/lib/upload";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import { useDeleteCalcDraftAttachment, useUploadCalcDraftAttachments } from "../../../../common/api/queries";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import FileTypeIconWithFilename from "../../../../common/components/icons/FileTypeIconWithFilename";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import { TableSizes } from "../../../../common/constants";
import { CALC_ROUTE_PATHS } from "../../paths";
import { CalcDraftAttachment, CalcDraftList } from "../types";

interface Props {
  calcDraft: CalcDraftList;
}

export const CalcDraftAttachmentsTableView = ({ calcDraft }: Props) => {
  const { t } = useTranslation();

  const uploadCalcDraftAttachments = useUploadCalcDraftAttachments();
  const deleteCalcDraftAttachment = useDeleteCalcDraftAttachment();

  const handleAttachmentUpload = (_: RcFile, fileList: RcFile[]) => {
    if (fileList.length > 0) {
      const formData = new FormData();
      fileList.forEach((file) => formData.append("files", file));
      fileList.splice(0, fileList.length);

      uploadCalcDraftAttachments.mutate({ id: calcDraft.id, object: formData });
    }

    return false;
  };

  const columns: ColumnsType<CalcDraftAttachment> = [
    {
      key: "filename",
      title: t("calc.draft.attrs.attachmentFilename"),
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <FileTypeIconWithFilename contentType={record.file.contentType} filename={record.file.filename} ellipsis />
      ),
    },
    {
      key: "createdAt",
      title: t("calc.draft.attrs.attachmentCreatedAt"),
      width: 160,
      render: (_, record) => formatLocaleDateTime(record.createdAt),
    },
    {
      key: "size",
      title: t("calc.draft.attrs.attachmentSize"),
      width: 100,
      render: (_, record) => formatFileSize(record.file.size),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon
            path={generatePath(CALC_ROUTE_PATHS.draftAttachment.to, { id1: calcDraft.id, id2: record.id })}
            target="_blank"
            icon="export"
            color="blue"
            text={t("common.open")}
          />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("calc.draft.titles.deleteAttachmentConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => deleteCalcDraftAttachment.mutate({ id1: calcDraft.id, id2: record.id })}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Row>
      <Col span={22} offset={1}>
        <ItemCreatedUpdatedInfoView item={calcDraft} className="margin-bottom-small" />

        <h3 className="margin-bottom-small">{t("calc.draft.titles.attachmentsList")}</h3>

        {calcDraft.attachments && calcDraft.attachments.length > 0 ? (
          <Table<CalcDraftAttachment>
            {...tableStandardProps()}
            columns={columns}
            scroll={{ x: TableSizes.SMALL }}
            dataSource={calcDraft.attachments}
            pagination={{ pageSize: 10, size: "small", hideOnSinglePage: true }}
          />
        ) : (
          <span className="sub-header-info dashed">{t("calc.draft.helpers.noAttachments")}</span>
        )}

        <div className="margin-top-medium">
          <UploadDragger multiple showUploadList={false} beforeUpload={handleAttachmentUpload} />
        </div>
      </Col>
    </Row>
  );
};
